import React, { useEffect, useState } from 'react';
import MyWhatsApp from '../../components/MyWhatsApp.jsx';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { Alert, Field } from '../../components/ui.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';
import { AccountHead, useAccount } from './AccountLayout.jsx';

const NOTIFY = ['messages', 'inquiries', 'listings', 'documents', 'offers', 'requestAlerts'];
// Company users (migration 030): workspace notices.
const TEAM_NOTIFY = ['assignments', 'docEvents', 'tasks'];

// Compte et sécurité: the member's own name and phone, sign-in email
// (changed with a 6-digit code sent to the new address), password, email
// language, which emails to receive, and deleting the account (typed
// confirmation on the page). In member mode the account-security changes
// are blocked by the server.
export default function SecurityPage() {
  const { t, errorText } = useI18n();
  const { user, refresh, signOut } = useAuth();
  const { reload } = useAccount();
  const navigate = useNavigate();
  usePageTitle('accountNav.security');
  const [settings, setSettings] = useState(null);
  const [me, setMe] = useState({ name: user.name || '', phone: user.phone || '' });
  const [msg, setMsg] = useState({ area: '', kind: '', text: '' });
  const [busy, setBusy] = useState('');
  const [email, setEmail] = useState({ step: 'idle', value: '', code: '' });
  const [del, setDel] = useState({ open: false, confirm: '' });
  const imp = !!user.impersonation;

  useEffect(() => { api('/account/settings').then(setSettings).catch((e) => setMsg({ area: 'top', kind: 'error', text: errorText(e) })); }, [errorText]);
  const say = (area, kind, text) => setMsg({ area, kind, text });
  const note = (area) => (msg.area === area ? <Alert kind={msg.kind || 'error'}>{msg.text}</Alert> : null);
  async function act(area, fn, okText) {
    setBusy(area); setMsg({ area: '', kind: '', text: '' });
    try { await fn(); if (okText) say(area, 'success', okText); } catch (e) { say(area, 'error', errorText(e)); } finally { setBusy(''); }
  }

  const saveMe = (e) => { e.preventDefault(); act('me', async () => {
    const p = await api('/profile');
    await api('/profile', { method: 'PATCH', body: { name: me.name, phone: me.phone, companyName: p.companyName || user.companyName, description: p.description || '', city: p.city || '', province: p.province || '' } });
    await refresh(); reload();
  }, t('common.saved')); };
  const sendCode = (e) => { e.preventDefault(); act('email', async () => {
    await api('/account/email/code', { method: 'POST', body: { newEmail: email.value } });
    setEmail((x) => ({ ...x, step: 'code' }));
  }, t('securityPage.codeSent', { email: email.value })); };
  const verifyCode = (e) => { e.preventDefault(); act('email', async () => {
    await api('/account/email/verify', { method: 'POST', body: { newEmail: email.value, code: email.code } });
    setEmail({ step: 'idle', value: '', code: '' });
    await refresh();
    setSettings((s) => ({ ...s, email: email.value.trim().toLowerCase() }));
  }, t('securityPage.emailChanged')); };
  const saveSettings = (body, area) => act(area, async () => setSettings(await api('/account/settings', { method: 'PATCH', body })), t('common.saved'));
  const deleteAccount = (e) => { e.preventDefault(); act('delete', async () => {
    await api('/account', { method: 'DELETE', body: { confirm: del.confirm } });
    signOut(); navigate('/', { replace: true });
  }); };

  return (
    <div className="acc-page">
      <AccountHead place="acc.security" title={t('accountNav.security')} lead={t('securityPage.lead')} />
      {imp && <div className="callout callout-info"><p>{t('securityPage.memberMode')}</p></div>}
      {note('top')}

      <form className="card form" onSubmit={saveMe}>
        <h2 className="acc-card-title">{t('securityPage.you')}</h2>
        <div className="grid-form">
          <Field label={t('auth.name')} htmlFor="se-name"><input id="se-name" value={me.name} onChange={(e) => setMe({ ...me, name: e.target.value })} required maxLength={120} autoComplete="name" /></Field>
          <Field label={t('auth.phone')} htmlFor="se-phone"><PhoneInput id="se-phone" value={me.phone} onChange={(v) => setMe({ ...me, phone: v })} required /></Field>
        </div>
        <p className="field-hint">{t('securityPage.youHint')}</p>
        {note('me')}
        <div><button type="submit" className="btn btn-primary" disabled={busy === 'me'}>{busy === 'me' ? t('common.saving') : t('common.save')}</button></div>
      </form>

      <section className="card form">
        <h2 className="acc-card-title">{t('securityPage.signIn')}</h2>
        <Field label={t('securityPage.currentEmail')} htmlFor="se-cur">
          <div className="readonly-field"><input id="se-cur" value={settings?.email || user.email} readOnly aria-readonly="true" />{user.emailVerified && <span className="badge badge-success">✓ {t('account.verified')}</span>}</div>
        </Field>
        {email.step === 'idle' && !imp && <div><button type="button" className="btn btn-ghost btn-sm" onClick={() => setEmail({ step: 'new', value: '', code: '' })}>{t('securityPage.changeEmail')}</button></div>}
        {email.step === 'new' && (
          <form className="acc-page" style={{ gap: 10 }} onSubmit={sendCode}>
            <Field label={t('securityPage.newEmail')} hint={t('securityPage.newEmailHint')} htmlFor="se-new"><input id="se-new" type="email" value={email.value} onChange={(e) => setEmail({ ...email, value: e.target.value })} required autoComplete="email" autoFocus /></Field>
            <div className="editor-actions" style={{ justifyContent: 'flex-start' }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy === 'email' || !email.value.includes('@')}>{t('securityPage.sendCode')}</button>
              <button type="button" className="btn btn-link btn-sm" onClick={() => setEmail({ step: 'idle', value: '', code: '' })}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
        {email.step === 'code' && (
          <form className="acc-page" style={{ gap: 10 }} onSubmit={verifyCode}>
            <Field label={t('securityPage.code', { email: email.value })} htmlFor="se-code"><input id="se-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={email.code} onChange={(e) => setEmail({ ...email, code: e.target.value.replace(/\D/g, '') })} autoFocus style={{ maxWidth: 180, letterSpacing: '0.3em', fontWeight: 700 }} /></Field>
            <div className="editor-actions" style={{ justifyContent: 'flex-start' }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy === 'email' || email.code.length !== 6}>{t('securityPage.confirm')}</button>
              <button type="button" className="btn btn-link btn-sm" onClick={() => setEmail({ ...email, step: 'new', code: '' })}>{t('securityPage.resend')}</button>
            </div>
          </form>
        )}
        {note('email')}
        <p className="small">{t('contactTab.password')} <Link to="/forgot-password">{t('contactTab.changePassword')}</Link>{settings?.googleLinked && ` ${t('securityPage.google')}`}</p>
      </section>

      <section className="card form">
        <h2 className="acc-card-title">{t('securityPage.language')}</h2>
        <p className="muted small">{t('securityPage.languageHint')}</p>
        <div className="acc-seg" role="radiogroup" aria-label={t('securityPage.language')}>
          {['fr', 'en'].map((l) => <button key={l} type="button" role="radio" aria-checked={settings?.lang === l} aria-pressed={settings?.lang === l} disabled={!settings || imp} onClick={() => saveSettings({ lang: l }, 'lang')}>{l === 'fr' ? 'Français' : 'English'}</button>)}
        </div>
        {note('lang')}
      </section>

      <section className="card form">
        <h2 className="acc-card-title">{t('securityPage.notifications')}</h2>
        <p className="muted small">{t('securityPage.notificationsHint')}</p>
        {settings && [...NOTIFY, ...(user?.team ? TEAM_NOTIFY : []), ...(user?.isSuperAdmin ? ['reviews'] : [])].map((k) => (
          <label key={k} className="acc-toggle" style={{ justifyContent: 'space-between', whiteSpace: 'normal', fontSize: '0.92rem', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
            <span><strong>{t(`securityPage.notify.${k}`)}</strong><br /><span className="muted small">{t(`securityPage.notifyHint.${k}`)}</span></span>
            <input type="checkbox" role="switch" checked={settings.notify[k]} disabled={imp || busy === 'notify'} onChange={(e) => saveSettings({ notify: { [k]: e.target.checked } }, 'notify')} />
          </label>
        ))}
        <p className="field-hint">{t('securityPage.alwaysSent')}</p>
        {note('notify')}
      </section>

      <MyWhatsApp disabled={imp} />

      {settings?.chatSettings && (
        <section className="card form">
          <h2 className="acc-card-title">{t('securityPage.chat')}</h2>
          <p className="muted small">{t('securityPage.chatHint')}</p>
          {['showOnline', 'findable'].map((k) => (
            <label key={k} className="acc-toggle" style={{ justifyContent: 'space-between', whiteSpace: 'normal', fontSize: '0.92rem', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
              <span><strong>{t(`securityPage.chatOpt.${k}`)}</strong><br /><span className="muted small">{t(`securityPage.chatOptHint.${k}`)}</span></span>
              <input type="checkbox" role="switch" checked={settings[k]} disabled={imp || busy === 'chat'} onChange={(e) => saveSettings({ [k]: e.target.checked }, 'chat')} />
            </label>
          ))}
          {note('chat')}
        </section>
      )}

      {settings?.canDelete && (
        <section className="card form" style={{ borderColor: '#f8c9d2' }}>
          <h2 className="acc-card-title" style={{ color: 'var(--red-600)' }}>{t('securityPage.delete')}</h2>
          <p className="muted small">{t('securityPage.deleteLead')}</p>
          {!del.open ? (
            <div><button type="button" className="btn btn-ghost btn-sm text-danger" onClick={() => setDel({ open: true, confirm: '' })} disabled={imp}>{t('securityPage.deleteStart')}</button></div>
          ) : (
            <form className="acc-confirm" onSubmit={deleteAccount} role="alertdialog" aria-labelledby="se-del-q">
              <p id="se-del-q">{t('securityPage.deleteConfirm', { email: settings.email })}</p>
              <input aria-label={t('securityPage.deleteType')} value={del.confirm} onChange={(e) => setDel({ ...del, confirm: e.target.value })} placeholder={settings.email} autoComplete="off" style={{ flex: '1 1 220px', background: '#fff' }} autoFocus />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDel({ open: false, confirm: '' })}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-danger btn-sm" disabled={busy === 'delete' || del.confirm.trim().toLowerCase() !== settings.email.toLowerCase()}>{t('securityPage.deleteFinal')}</button>
            </form>
          )}
          {note('delete')}
        </section>
      )}
    </div>
  );
}
